const REVEAL_SELECTOR = [
  '#MainContent .product-grid__item',
  '#MainContent .resource-list__item',
  '#MainContent .resource-list__slide',
  '#MainContent .media-with-content > .media-block',
  '#MainContent .media-with-content > .media-with-content__content',
  '#MainContent .text-block',
  '#MainContent .jdgm-widget',
  '#MainContent .jdgm-all-reviews-widget',
  '#MainContent .jdgm-carousel-wrapper',
  '#MainContent #judgeme_product_reviews',
].join(',');

const PRODUCT_CARD_CONTENT_SELECTOR = 'product-card, .product-card';
const TEXT_REVEAL_EXCLUDED_ANCESTOR_SELECTOR = [
  PRODUCT_CARD_CONTENT_SELECTOR,
  '.collection-card',
  '.resource-card',
  '.product-grid__item',
  '.resource-list__item',
  '.resource-list__slide',
].join(',');

const REVEALED_CLASS = 'is-scroll-revealed';
const REVEAL_ATTRIBUTE = 'data-scroll-reveal';
const MAX_STAGGER_INDEX = 7;
const STAGGER_INTERVAL = 70;

const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const canObserveIntersections = 'IntersectionObserver' in window;

/** @type {IntersectionObserver | undefined} */
let revealObserver;
/** @type {MutationObserver | undefined} */
let mutationObserver;
/** @type {Set<Element>} */
let pendingRoots = new Set();
let pendingFrame = 0;

function isThemeEditor() {
  return document.documentElement.classList.contains('shopify-design-mode') || Boolean(window.Shopify?.designMode);
}

function shouldRevealImmediately() {
  return motionQuery.matches || isThemeEditor() || !canObserveIntersections;
}

/**
 * @param {Element} element
 */
function revealElement(element) {
  element.classList.add(REVEALED_CLASS);
  revealObserver?.unobserve(element);
}

function createRevealObserver() {
  if (!canObserveIntersections || shouldRevealImmediately()) return;

  revealObserver =
    revealObserver ||
    new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            revealElement(entry.target);
          }
        }
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08,
      }
    );
}

/**
 * @param {HTMLElement} target
 */
function isExcludedRevealTarget(target) {
  return target.matches('#MainContent .text-block') && Boolean(target.closest(TEXT_REVEAL_EXCLUDED_ANCESTOR_SELECTOR));
}

/**
 * @param {ParentNode | Element} root
 * @returns {HTMLElement[]}
 */
function getRevealTargets(root) {
  const targets = [];

  if (root instanceof HTMLElement && root.matches(REVEAL_SELECTOR)) {
    targets.push(root);
  }

  if ('querySelectorAll' in root) {
    targets.push(...root.querySelectorAll(REVEAL_SELECTOR));
  }

  return Array.from(new Set(targets)).filter(
    (target) =>
      target instanceof HTMLElement && !target.closest('[hidden], dialog:not([open])') && !isExcludedRevealTarget(target)
  );
}

/**
 * @param {HTMLElement[]} targets
 */
function prepareTargets(targets) {
  const staggerIndexes = new WeakMap();

  for (const target of targets) {
    if (target.hasAttribute(REVEAL_ATTRIBUTE)) continue;

    const parent = target.parentElement;
    const currentIndex = parent ? staggerIndexes.get(parent) || 0 : 0;
    const staggerIndex = Math.min(currentIndex, MAX_STAGGER_INDEX);

    target.setAttribute(REVEAL_ATTRIBUTE, '');
    target.style.setProperty('--scroll-reveal-stagger', `${staggerIndex * STAGGER_INTERVAL}ms`);

    if (parent) {
      staggerIndexes.set(parent, currentIndex + 1);
    }

    if (shouldRevealImmediately()) {
      revealElement(target);
      continue;
    }

    revealObserver?.observe(target);
  }
}

/**
 * @param {Element | Document} root
 */
function refreshScrollReveal(root = document) {
  createRevealObserver();
  prepareTargets(getRevealTargets(root));
}

function flushPendingRoots() {
  const roots = Array.from(pendingRoots);
  pendingRoots = new Set();
  pendingFrame = 0;

  for (const root of roots) {
    refreshScrollReveal(root);
  }
}

/**
 * @param {Element} root
 */
function queueRefresh(root) {
  pendingRoots.add(root);

  if (pendingFrame) return;

  pendingFrame = window.requestAnimationFrame(flushPendingRoots);
}

function observeNewContent() {
  const mainContent = document.getElementById('MainContent');
  if (!mainContent || mutationObserver) return;

  mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          queueRefresh(node);
        }
      }
    }
  });

  mutationObserver.observe(mainContent, {
    childList: true,
    subtree: true,
  });
}

function initScrollReveal() {
  refreshScrollReveal(document);
  observeNewContent();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollReveal, { once: true });
} else {
  initScrollReveal();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    queueRefresh(event.target);
  }
});

motionQuery.addEventListener('change', () => {
  if (!motionQuery.matches) return;

  for (const target of document.querySelectorAll(`[${REVEAL_ATTRIBUTE}]`)) {
    revealElement(target);
  }
});
